import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import HomePage from "@/pages/HomePage";
import TrackingPage from "@/pages/TrackingPage";
import RepairRequestPage from "@/pages/RepairRequestPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CheckoutPage from "@/pages/CheckoutPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPending from "@/pages/admin/AdminPending";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminNewOS from "@/pages/admin/AdminNewOS";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminRankings from "@/pages/admin/AdminRankings";
import AdminStores from "@/pages/admin/AdminStores";
import AdminSplitReport from "@/pages/admin/AdminSplitReport";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminBoard from "@/pages/admin/AdminBoard";
import AdminEmails from "@/pages/admin/AdminEmails";
import AdminCancellations from "@/pages/admin/AdminCancellations";
import AdminShipments from "@/pages/admin/AdminShipments";
import LabelPrint from "@/pages/admin/LabelPrint";
import MagicLinkVerify from "@/pages/MagicLinkVerify";
import AuthCallback from "@/pages/AuthCallback";
import ResetPasswordRequest from "@/pages/ResetPasswordRequest";
import ResetPasswordConfirm from "@/pages/ResetPasswordConfirm";
import { StoreProvider } from "@/lib/stores";

function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-neutral-500">Carregando…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (adminOnly && user.role !== "admin" && user.role !== "superadmin") return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  // Detect Emergent Google Auth callback (session_id in URL fragment).
  // Synchronous check during render prevents race with AuthProvider.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/rastrear/:code" element={<TrackingPage />} />
      <Route path="/solicitar-reparo" element={<RepairRequestPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/cadastro" element={<RegisterPage />} />
      <Route path="/auth/magic" element={<MagicLinkVerify />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/esqueci-senha" element={<ResetPasswordRequest />} />
      <Route path="/auth/redefinir-senha" element={<ResetPasswordConfirm />} />
      <Route path="/minha-conta" element={<Protected><CustomerDashboard /></Protected>} />
      <Route path="/checkout/:code" element={<CheckoutPage />} />
      <Route path="/pagamento/sucesso" element={<PaymentSuccess />} />
      {/* /admin/login mantido como redirect pra rota canônica /login (retro-compat pra bookmarks e links antigos) */}
      <Route path="/admin/login" element={<Navigate to="/login" replace />} />
      <Route path="/admin" element={<Protected adminOnly><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="kanban" element={<AdminBoard />} />
        <Route path="pendentes" element={<AdminPending />} />
        <Route path="pagamentos" element={<AdminPayments />} />
        <Route path="nova-os" element={<AdminNewOS />} />
        <Route path="rankings" element={<AdminRankings />} />
        <Route path="lojas" element={<AdminStores />} />
        <Route path="relatorios/split" element={<AdminSplitReport />} />
        <Route path="integracoes" element={<AdminIntegrations />} />
        <Route path="configuracoes" element={<AdminSettings />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="cancelamentos" element={<AdminCancellations />} />
        <Route path="envios" element={<AdminShipments />} />
        <Route path="auditoria" element={<AdminAudit />} />
      </Route>
      <Route path="/admin/etiqueta/:code" element={<Protected adminOnly><LabelPrint /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
